/**
 * CostTable — honest ballpark monthly cost estimates for the Phase 1 chassis.
 */
import { useReveal } from '../lib/anim/useReveal';

type CostRow = {
  load: string;
  tenants: string;
  cost: string;
  notes: string;
};

const ROWS: CostRow[] = [
  {
    load: 'Small',
    tenants: '1–10',
    cost: '$5–20 / mo',
    notes: 'Mostly free tiers. Stripe processing fees + tiny Anthropic usage + Supabase free plan.',
  },
  {
    load: 'Medium',
    tenants: '50–100',
    cost: '$80–150 / mo',
    notes: 'Supabase Pro $25, Workers paid $5, Vectorize paid, Anthropic ~$30, Resend $20, Stripe processing.',
  },
  {
    load: 'Large',
    tenants: '500+',
    cost: '$400–800 / mo',
    notes: 'Supabase Pro + compute add-ons, Workers higher-volume, Anthropic becomes the dominant line item.',
  },
];

export function CostTable() {
  const reveal = useReveal();

  return (
    <section className="py-20" style={{ background: 'var(--color-bg)' }}>
      <div className="max-w-4xl mx-auto px-6">
        <div
          ref={reveal.ref}
          {...reveal.props}
          className={`mb-10 text-center${reveal.props.className ? ' ' + reveal.props.className : ''}`}
        >
          <p className="text-sm uppercase tracking-widest mb-3 font-medium" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>
            Cost transparency
          </p>
          <h2 className="text-2xl md:text-3xl font-bold text-ink" style={{ fontFamily: 'var(--font-display)' }}>
            What the chassis actually costs.
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-sm leading-relaxed" style={{ color: 'var(--color-mute)' }}>
            These are real ballpark numbers for the Phase 1 stack. They cover the chassis only —
            Onwards' own time and labour are separate. Per-tenant marginal cost is dominated by
            Anthropic tokens, not infra.
          </p>
        </div>

        <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid var(--color-hairline)' }}>
          <table className="w-full text-sm text-left">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--color-hairline)', background: 'var(--color-surface)' }}>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-widest w-24" style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-mono)' }}>
                  Load
                </th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-widest w-28" style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-mono)' }}>
                  Tenants
                </th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-widest w-36" style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-mono)' }}>
                  Approx / mo (USD)
                </th>
                <th className="px-5 py-4 text-xs font-semibold uppercase tracking-widest" style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-mono)' }}>
                  Notes
                </th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row, i) => (
                <tr
                  key={row.load}
                  className="transition-colors"
                  style={{ borderBottom: i < ROWS.length - 1 ? '1px solid var(--color-hairline)' : undefined }}
                >
                  <td className="px-5 py-4 font-semibold" style={{ color: 'var(--color-ink)' }}>{row.load}</td>
                  <td className="px-5 py-4 tabular-nums" style={{ color: 'var(--color-mute)', fontFamily: 'var(--font-mono)' }}>{row.tenants}</td>
                  <td className="px-5 py-4 font-medium tabular-nums whitespace-nowrap" style={{ color: 'var(--color-accent)', fontFamily: 'var(--font-mono)' }}>
                    {row.cost}
                  </td>
                  <td className="px-5 py-4 leading-relaxed" style={{ color: 'var(--color-mute)' }}>{row.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-xs text-center" style={{ color: 'var(--color-mute)' }}>
          All prices USD. Cost estimates reflect Phase 1 (pre-scale) usage patterns and will shift as
          tenants grow. Onwards bills you at the Clarity subscription rate — chassis costs are on us.
        </p>
      </div>
    </section>
  );
}
